import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generateDefaultCreateStyle } from 'containers/create-style/AddCreateStyleItem/FormCreateStyle';
import { v4 as uuidV4 } from 'uuid';
import { ItemStatus } from '../../containers/style_detail/constant';
import { StorePlatform } from '../../containers/create-style/AddOrSelectItem/type';

export interface ISingleImage {
    url: string;
    imageCanvas: string;
    id: string;
    idCreateStyle?: string;
    left: number;
    top: number;
    angle: number;
    height: number;
    width: number;
    scaleX: number;
    scaleY: number;
    type?: string;
    text?: string;
    fontColor?: string;
    fontFamily?: string;
}

export interface IQuestionData {
    id?: string;
    subject?: string;
    title?: string;
    value: string;
    type?: string;
}

export interface ITagData {
    id?: string;
    name?: string;
    value?: string;
}

export interface IBrandData {
    id?: string;
    name?: string;
    value?: string;
}

export interface IBackgroundRemovedMap {
    originalUrl: string;
    backgroundRemovedUrl: string;
}

export interface ICreateStyle {
    id?: string;
    url: string;
    title: string;
    description: string;
    price: string | number;
    currency: string;
    itemCodes: string;
    colorsId: IQuestionData[];
    sizesId: IQuestionData[];
    tagsId: ITagData[];
    accessLevel: string;
    storeId?: string;
    storeProductId?: string;
    creator: string;
    user?: any;
    brandId?: IBrandData;
    backgroundRemovedMap?: IBackgroundRemovedMap[];
}

export interface IItemVariant {
    color: string;
    size: string;
}

export interface IItemData {
    id: string;
    data: ICreateStyle;
    variant: IItemVariant;
}

export interface IGroupImage {
    id: string;
    listImages: ISingleImage[];
}

export interface ICreateStyleItem {
    id: string;
    createStyleInfo: ICreateStyle;
    listImages: ISingleImage[];
    imageCanvas: string;
    imageUrl: string;
    selectImageIndex: any;
}

export const generateDefaultCreateStyleItem = (): ICreateStyleItem => ({
    id: uuidV4(),
    createStyleInfo: generateDefaultCreateStyle(),
    listImages: [],
    imageCanvas: '',
    imageUrl: '',
    selectImageIndex: 0,
});

// export interface

export interface ISelectedImage {
    idCreateStyle: string;
    idSingleImage: string;
}

export interface ICategoryData {
    id?: string;
    name: string;
}

export type IStyleInformation = {
    title: string;
    description: string;
    categoriesId: ICategoryData[];
    purposesId: string[];
    agesId: string[];
    seasonsId: string[];
    gendersId: string[];
    bodyShapesId: string[];
    bodyTypesId: string[];
    personasId: string[];
    tagsId: ITagData[];
    status: ItemStatus;
};

export interface IStore {
    id: string;
    name: string;
    url: string;
    logoImageUrl: string;
    domain: string;
    shopifyStorefrontToken: string;
    affiliateLink: string;
    platform: StorePlatform;
    useTaffiItems?: boolean;
}

export const generateDefaultStyleInformation = (): IStyleInformation => {
    return {
        title: '',
        description: '',
        categoriesId: [],
        purposesId: [],
        agesId: [],
        seasonsId: [],
        gendersId: [],
        bodyShapesId: [],
        bodyTypesId: [],
        personasId: [],
        tagsId: [],
        status: ItemStatus.Draft,
    };
};

export type CreateStyleState = {
    currentEditingStyleId: string | null;
    currentEditingItemId: string | null;
    currentItem: ICreateStyle | null;
    canvasObject: any | null;
    lastCreatedStyleConsumers: any | null;
    items: IItemData[] | null;
    imageJson: ISingleImage[] | null;
    lastCreatedStyleTaffiId: string | null;
    lastCreatedStyleStatus: string | null;
    createStyleCanvasImageUrl: string | null;
    finalImageBlob: Blob | null;
    styleImg: any;
    currentFormData: any;
    renderRequested: boolean;
    activeImageIndex: number | undefined;
    lastTimeImageJsonDirty: number;
    lastTimeImageJsonUpdated: number;
    editStyleItemDisabled: boolean;
    selectedStore: IStore | null;
    queryStoreCursor: string | null;
};
const defaultDate = new Date().valueOf();
const initialState: CreateStyleState = {
    currentEditingStyleId: null,
    currentEditingItemId: null,
    currentItem: null,
    canvasObject: null,
    items: null,
    imageJson: null,
    lastCreatedStyleTaffiId: null,
    lastCreatedStyleStatus: ItemStatus.Draft,
    lastCreatedStyleConsumers: null,
    createStyleCanvasImageUrl: null,
    finalImageBlob: null,
    styleImg: null,
    currentFormData: {
        tags: [],
    },
    renderRequested: false,
    activeImageIndex: undefined,
    lastTimeImageJsonDirty: defaultDate,
    lastTimeImageJsonUpdated: defaultDate,
    editStyleItemDisabled: false,
    selectedStore: null,
    queryStoreCursor: null,
};

export const generateDefaultSingleImage = (): ISingleImage => {
    return {
        id: uuidV4(),
        url: '',
        imageCanvas: '',
        top: 200,
        left: 200,
        angle: 0,
        height: 400,
        width: 200,
        scaleX: 1,
        scaleY: 1,
    };
};

const createStyleSlice = createSlice({
    name: 'createStyle',
    initialState,
    reducers: {
        addCreateStyleCanvasImage: (state, action: PayloadAction<{ url: string | null }>) => {
            const { url } = action.payload;

            state.createStyleCanvasImageUrl = url;
        },
        setStyleImg: (state, action) => {
            state.styleImg = action.payload;
        },
        addFinalImageBlob: (
            state,
            action: PayloadAction<{
                blob: Blob | null;
            }>,
        ) => {
            state.finalImageBlob = action.payload.blob;
        },
        setLastCreatedStyleInfo(
            state,
            action: PayloadAction<{
                taffiId: string;
                status: string;
                consumers?: any;
            }>,
        ) {
            state.lastCreatedStyleTaffiId = action.payload.taffiId;
            state.lastCreatedStyleStatus = action.payload.status;
            if (action.payload.consumers) state.lastCreatedStyleConsumers = action.payload.consumers;
        },
        clearLastCreatedStyleInfo: (state) => {
            state.lastCreatedStyleTaffiId = null;
            state.lastCreatedStyleStatus = null;
        },
        setRenderRequested: (state, action) => {
            state.renderRequested = action.payload;
        },
        setActiveImageIndex: (state, action) => {
            state.activeImageIndex = action.payload;
        },
        setLastTimeImageJsonDirty: (state, action) => {
            state.lastTimeImageJsonDirty = action.payload;
        },
        setLastTimeImageJsonUpdated: (state, action) => {
            state.lastTimeImageJsonUpdated = action.payload;
        },
        setCurrentEditingStyleId: (state, action) => {
            state.currentEditingStyleId = action.payload;
        },
        setCurrentEditingItemId: (state, action) => {
            state.currentEditingItemId = action.payload;
        },
        setStyleCanvasObject: (state, action) => {
            state.canvasObject = action.payload;
        },
        setStyleItems: (state, action: PayloadAction<IItemData[]>) => {
            state.items = action.payload;
        },
        setStyleItemsAndImageJson: (
            state,
            action: PayloadAction<{
                items: IItemData[];
                imageJson: ISingleImage[];
            }>,
        ) => {
            state.items = action.payload.items;
            state.imageJson = action.payload.imageJson;
        },
        updateItem: (state, action) => {
            const updateData = action.payload;
            if (Array.isArray(state.items)) {
                state.items = state.items.map((item) => {
                    if (item.id === updateData.id) {
                        return {
                            ...item,
                            ...updateData,
                        };
                    }
                    return { ...item };
                });
            }
        },
        setImageJson: (state, action: PayloadAction<ISingleImage[]>) => {
            state.imageJson = action.payload;
        },
        setEditStyleItemDisabled: (state, action) => {
            state.editStyleItemDisabled = action.payload;
        },
        setSelectedStore: (state, action: PayloadAction<IStore | null>) => {
            state.selectedStore = action.payload;
        },
        setQueryStoreCursor: (state, action: PayloadAction<string | null>) => {
            state.queryStoreCursor = action.payload;
        },
    },
    extraReducers: {},
});

const createStyleReducer = createStyleSlice.reducer;
export const {
    addCreateStyleCanvasImage,
    addFinalImageBlob,
    setLastCreatedStyleInfo,
    clearLastCreatedStyleInfo,
    setStyleImg,
    setRenderRequested,
    setActiveImageIndex,
    setLastTimeImageJsonDirty,
    setLastTimeImageJsonUpdated,
    setCurrentEditingStyleId,
    setCurrentEditingItemId,
    setStyleCanvasObject,
    setStyleItems,
    updateItem,
    setImageJson,
    setEditStyleItemDisabled,
    setStyleItemsAndImageJson,
    setSelectedStore,
    setQueryStoreCursor,
} = createStyleSlice.actions;
export default createStyleReducer;
